import React from "react";
import Button from "components/button/Button";
import ProductCard from "components/card/ProductCard";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import { TiPlus } from "react-icons/ti";
import { deleteGood, getGoods } from "api/GoodsAPI";
import { toast } from "react-hot-toast";
import { IGoods } from "./types/goods.interface";
import { Spinner } from "@chakra-ui/react";
import { FiSearch } from "react-icons/fi";
import { debounce } from "lodash";
import { BiChevronLeft, BiChevronRight } from "react-icons/bi";
import { PiDotsThreeBold } from "react-icons/pi";
import { NumberParam, StringParam, useQueryParams } from "use-query-params";
import { OrderBy } from "constants/orderBy";

const orderByOptions: {
  value: number;
  label: string;
}[] = [
  { value: OrderBy.Terbaru, label: "Terbaru" },
  { value: OrderBy.Terupdate, label: "Terupdate" },
  { value: OrderBy.Termurah, label: "Termurah" },
  { value: OrderBy.Termahal, label: "Termahal" },
];

const totalItemsOptions: {
  value: number;
}[] = [
  { value: 10 },
  { value: 20 },
  { value: 25 },
  { value: 50 },
  { value: 100 },
];

const Goods = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [goods, setGoods] = React.useState<IGoods[]>([]);
  const [query, setQuery] = useQueryParams({
    page: NumberParam,
    ob: NumberParam,
    search: StringParam,
    limit: NumberParam,
  });

  const [orderBy, setOrderBy] = React.useState<number>(null);
  const [limit, setLimit] = React.useState<number>(null);
  const [searchKey, setSearchKey] = React.useState<string>(null);
  const [nextPage, setNextPage] = React.useState<number>(null);
  const [prevPage, setPrevPage] = React.useState<number>(null);
  const [lastPage, setLastPage] = React.useState<number>(null);
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [pageState, setPageState] = React.useState({
    startIdx: 0,
    endIdx: 0,
  });
  const [pageNumbers, setPageNumbers] = React.useState<number[]>([]);

  const jumpForwardPage = () => {
    setPageState({
      startIdx: pageState.endIdx,
      endIdx: pageState.endIdx + 5,
    });
  };

  const jumpBackwardPage = () => {
    setPageState({
      startIdx: pageState.startIdx - 5,
      endIdx: pageState.endIdx - 5,
    });
  };

  const handleNextPage = () => {
    if (currentPage === lastPage) return;
    setCurrentPage(nextPage);
    getGoodsWithParams(nextPage, orderBy, searchKey, limit);
  };

  const handlePrevPage = () => {
    if (currentPage === 1) return;
    setCurrentPage(prevPage);
    getGoodsWithParams(prevPage, orderBy, searchKey, limit);
  };

  const handleSearchKey = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsLoading(true);
    setSearchKey(e.target.value === "" ? null : e.target.value);
    getGoodsWithParams(currentPage, orderBy, e.target.value, limit);
    setQuery({
      page: currentPage,
      ob: query.ob,
      search: e.target.value === "" ? null : e.target.value,
      limit: query.limit,
    });
  };

  const debounceRes = React.useMemo(() => {
    return debounce(handleSearchKey, 500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getGoodsWithParams = (
    page?: number,
    order?: number,
    keyword?: string,
    limit?: number
  ): void => {
    setIsLoading(true);
    const promise = getGoods(page, order, keyword, limit);

    promise
      .then((res) => {
        if (res.status === 200) {
          setIsLoading(false);
          setGoods(res.data.data.items);
          setNextPage(res.data.data.meta.next_page);
          setPrevPage(res.data.data.meta.prev_page);
          setLastPage(res.data.data.meta.last_page);
          setCurrentPage(res.data.data.meta.current_page);
          setPageNumbers(
            Array.from(Array(res.data.data.meta.last_page + 1).keys()).slice(1)
          );
          if (res.data.data.meta.current_page === pageState.endIdx + 1) {
            setPageState({
              startIdx: pageState.endIdx,
              endIdx: pageState.endIdx + 5,
            });
          } else if (res.data.data.meta.current_page === pageState.startIdx) {
            setPageState({
              startIdx: pageState.startIdx - 5,
              endIdx: pageState.endIdx - 5,
            });
          }
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Gagal memuat barang!");
      });
  };

  const handleDelete = (slug: string): void => {
    const promise = deleteGood(slug);

    toast.promise(promise, {
      loading: "Menghapus barang...",
      success: (res) => {
        if (res.status === 200) {
          getGoodsWithParams(currentPage, orderBy, searchKey, limit);
        }

        return res.data.message || "Berhasil menghapus barang!";
      },
      error: "Gagal menghapus barang!",
    });
  };

  React.useEffect(() => {
    getGoodsWithParams(query.page, query.ob, query.search, query.limit);
    setPageState({
      startIdx: 0,
      endIdx: 5,
    });

    return () => {
      debounceRes.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="mt-3 grid h-full grid-cols-1">
      <div className="col-span-1 h-fit w-full">
        {/* Recenlty Added setion */}
        <div className="mb-5 mt-5 flex items-center justify-between px-[26px]">
          <h4 className="text-2xl font-bold text-navy-700 dark:text-white">
            Barang
          </h4>
          <div className="flex gap-3">
            <Button
              size="fit"
              variant="outlined"
              children={
                <div className="flex items-center gap-1">
                  <TiPlus /> Barang dengan CSV
                </div>
              }
              onClick={() => {
                navigate("/admin/goods/add/csv");
              }}
            />
            <Button
              size="fit"
              variant="primary"
              children={
                <div className="flex items-center gap-1">
                  <TiPlus /> Barang
                </div>
              }
              onClick={() => {
                navigate("/admin/goods/add");
              }}
            />
          </div>
        </div>
        <div className="mb-5 flex w-full items-center justify-between gap-8 px-[26px]">
          <div className="col-span-8 flex h-12 w-full flex-3 items-center rounded-full border-2 border-gray-300 bg-gray-100 text-navy-700 dark:bg-navy-900 dark:text-white">
            <p className="pl-3 pr-2 text-xl">
              <FiSearch className="h-4 w-4 text-gray-400 dark:text-white" />
            </p>
            <input
              type="text"
              placeholder="Coba cari nama barang..."
              onChange={(e) => debounceRes(e)}
              defaultValue={searchKey || query.search}
              className="block h-full w-full rounded-full bg-gray-100 text-sm font-medium text-navy-700 outline-none placeholder:!text-gray-400 dark:bg-navy-900 dark:text-white dark:placeholder:!text-white"
            />
          </div>
          <div className="flex-1 flex items-center gap-3">
            <div className="flex cursor-pointer items-center gap-2">
              <Select
                options={orderByOptions}
                placeholder="Urutkan"
                classNames={{
                  placeholder: () => `!text-brand-500 dark:text-white/50`,
                  control: () =>
                    `flex !h-12 w-full items-center justify-center !rounded-3xl font-semibold !bg-white !pb-3 !pl-3 !pt-1 text-sm !outline-none transition-all duration-300 !border-none dark:!border-white/10 dark:text-white !cursor-pointer`,
                  container: () => `w-32`,
                  dropdownIndicator: () => `!text-brand-500`,
                  indicatorSeparator: () => `!hidden`,
                  option: () => `!cursor-pointer`,
                }}
                onChange={(val) => {
                  setOrderBy(val?.value || 0);
                  getGoodsWithParams(
                    currentPage,
                    val?.value || 0,
                    searchKey,
                    limit
                  );
                  setQuery({
                    page: currentPage,
                    ob: val?.value || 0,
                    search: searchKey,
                    limit: limit,
                  });
                }}
              />
            </div>
            <div className="flex items-center gap-2">
              <Select
                options={totalItemsOptions}
                placeholder="Total Item"
                classNames={{
                  placeholder: () => `!text-brand-500 dark:text-white/50`,
                  control: () =>
                    `flex !h-12 w-full items-center justify-center !rounded-3xl font-semibold !bg-white !pb-3 !pl-3 !pt-1 text-sm !outline-none transition-all duration-300 !border-none dark:!border-white/10 dark:text-white !cursor-pointer`,
                  container: () => `w-32`,
                  dropdownIndicator: () => `!text-brand-500`,
                  indicatorSeparator: () => `!hidden`,
                  option: () => `!cursor-pointer`,
                }}
                getOptionLabel={(option) => `${option.value} item`}
                onChange={(val) => {
                  setLimit(val?.value || 10);
                  getGoodsWithParams(
                    currentPage,
                    orderBy,
                    searchKey,
                    val?.value || 10
                  );
                  setQuery({
                    page: currentPage,
                    ob: orderBy,
                    search: searchKey,
                    limit: val?.value || 10,
                  });
                }}
              />
            </div>
          </div>
        </div>

        {/* NFTs trending card */}
        {!isLoading ? (
          goods?.length > 0 ? (
            <div className="z-20 grid grid-cols-1 gap-5 md:grid-cols-3 xl:grid-cols-2 2xl:grid-cols-3">
              {goods.map((good, idx) => (
                <div key={idx}>
                  <ProductCard
                    id={good.id.toString()}
                    title={good.name}
                    category={good.category ?? good.category_detail}
                    category_detail={good.category_detail ?? good.category}
                    price={good.price}
                    sku={good.sku}
                    description={good.description}
                    thumbnail={good.image_thumbnail}
                    images={good.images}
                    slug={good.slug}
                    onDelete={() => handleDelete(good.slug)}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="flex h-80 items-center justify-center">
              <p className="text-lg font-medium text-gray-600 dark:text-gray-400">
                {!searchKey
                  ? "Belum ada barang yang ditambahkan"
                  : "Barang tidak ditemukan"}
              </p>
            </div>
          )
        ) : (
          <div className="flex h-80 w-full flex-col items-center justify-center">
            <Spinner size="xl" className="text-brand-400" />
          </div>
        )}
        {!isLoading && goods?.length > 0 && (
          <div className="pagination my-5 flex w-full justify-center">
            <div className="pagination__item flex items-center gap-2">
              <button
                className="pagination__button"
                onClick={() => handlePrevPage()}
              >
                <BiChevronLeft className="text-xl" />
              </button>
              <div className="flex items-center justify-center gap-1">
                {pageState.startIdx >= 1 && (
                  <button
                    className="pagination__button"
                    onClick={() => jumpBackwardPage()}
                  >
                    <PiDotsThreeBold className="text-lg" />
                  </button>
                )}
                {pageNumbers
                  .slice(pageState.startIdx, pageState.endIdx)
                  .map((page, idx) => (
                    <button
                      className={`pagination__button ${
                        currentPage === page ? "pagination__button--active" : ""
                      }`}
                      onClick={() => {
                        setCurrentPage(page);
                        getGoodsWithParams(page, orderBy, searchKey, limit);
                        setQuery({
                          page: page,
                          ob: orderBy,
                          search: searchKey,
                          limit: limit,
                        });
                      }}
                      key={idx}
                    >
                      {page}
                    </button>
                  ))}
                {pageState.endIdx <= lastPage && (
                  <button
                    className="pagination__button"
                    onClick={() => jumpForwardPage()}
                  >
                    <PiDotsThreeBold className="text-lg" />
                  </button>
                )}
              </div>
              <button
                className="pagination__button"
                onClick={() => handleNextPage()}
              >
                <BiChevronRight className="text-xl" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* right side section */}

      {/* <div className="col-span-1 h-full w-full rounded-xl 3xl-max:!order-first">
        <CategoriesCard />
      </div> */}
    </div>
  );
};

export default Goods;
